/**
 * Human-readable Master Admin header titles (no route IDs).
 * Match more specific paths before generic `/vendors/:kind/:id`.
 */
export function getMasterAdminPageTitle(pathname: string): string {
  if (pathname === '/' || pathname === '/master-admin') return 'Dashboard';
  if (pathname === '/vendors') return 'Vendor Management';
  if (pathname === '/users') return 'User Management';
  if (pathname === '/reports') return 'API and error report';
  if (pathname === '/insights/her-phases-analytics') return 'Her Phases Analytics';
  if (pathname === '/billing/overview') return 'Billing Overview';
  if (pathname === '/billing/services') return 'Billing Services';
  if (pathname === '/billing/resources') return 'Billing Resources';
  if (pathname === '/billing/history') return 'Billing History';
  if (pathname === '/monitoring') return 'Data Monitoring';
  if (pathname === '/operations/production-dashboard') return 'Production Operations';
  if (pathname === '/insights/app-tracking') return 'App Tracking Insights';
  if (pathname === '/health-record-usage') return 'User Storage';
  if (pathname === '/vendor-storage') return 'Vendor Storage';
  if (pathname === '/deliveries/borzo') return 'Borzo Deliveries';
  if (pathname === '/settings') return 'Settings';
  if (pathname === '/help') return 'Help & Support';
  if (pathname === '/employees') return 'Team Access';
  if (/^\/employees\/member\/[^/]+$/.test(pathname)) return 'Team member access';
  if (pathname === '/in-progress-notify') return 'Push notifications';
  if (pathname === '/push-notifications' || pathname.startsWith('/push-notifications/')) {
    if (pathname.includes('/campaigns/new')) return 'Create push campaign';
    if (pathname.includes('/campaigns/') && pathname.includes('/edit')) return 'Edit push campaign';
    if (pathname.includes('/templates/new')) return 'Create push template';
    if (pathname.includes('/templates/') && pathname.includes('/edit')) return 'Edit push template';
    if (pathname.includes('/templates')) return 'Push templates';
    if (pathname.includes('/recipients')) return 'Push recipients';
    if (pathname.includes('/delivery')) return 'Push delivery';
    if (pathname.includes('/analytics')) return 'Push analytics';
    return 'Push notifications';
  }
  if (pathname === '/operations/marketing-emails') return 'Marketing emails';
  if (pathname === '/operations/whatsapp' || pathname.startsWith('/operations/whatsapp/')) {
    if (pathname.includes('/testing')) return 'WhatsApp testing';
    if (pathname.includes('/campaigns')) return 'WhatsApp campaigns';
    if (pathname.includes('/analytics')) return 'WhatsApp analytics';
    return 'WhatsApp inbox';
  }
  if (pathname === '/notifications') return 'Notifications';
  if (pathname === '/feedbacks') return 'Feedbacks';
  if (pathname === '/grievances') return 'Grievances';
  if (/^\/grievances\/[^/]+\/policy\/vendor$/.test(pathname)) return 'Vendor policies';
  if (/^\/grievances\/[^/]+\/policy\/user$/.test(pathname)) return 'User cancellation policy';
  if (/^\/grievances\/[^/]+\/resolve$/.test(pathname)) return 'Resolve grievance';
  if (pathname === '/app-version-control') return 'App version control';
  if (pathname === '/app-version-control/notifications') return 'App update notifications';
  if (pathname === '/request-queue') return 'Request Queue';
  if (pathname === '/ambulance-bookings') return 'Ambulance Bookings';
  if (pathname === '/ambulance-off-hours-demand') return 'Ambulance off-hours demand';
  if (pathname === '/ambulance-service-areas') return 'Ambulance service areas';
  if (pathname === '/operations/exotel-calling' || pathname.startsWith('/operations/exotel-calling/')) {
    if (pathname.endsWith('/settings')) return 'Exotel Calling settings';
    if (pathname.endsWith('/activity')) return 'Call activity';
    if (pathname.endsWith('/calls')) return 'Place a call';
    if (pathname.endsWith('/webhooks')) return 'Webhook logs';
    return 'Exotel Calling';
  }
  if (pathname === '/operations/communications' || pathname.startsWith('/operations/communications/')) {
    if (pathname.endsWith('/settings')) return 'Exotel Calling settings';
    if (pathname.endsWith('/activity')) return 'Call activity';
    if (pathname.endsWith('/calls')) return 'Place a call';
    if (pathname.endsWith('/webhooks')) return 'Webhook logs';
    return 'Exotel Calling';
  }
  if (pathname === '/operations/exotel-settings') return 'Exotel Calling settings';
  if (pathname === '/insights/call-routing') return 'Call activity';
  if (pathname === '/master-admin/operations/manual-refund-cases') return 'Manual refund cases';
  if (pathname === '/operations/manual-refund-cases') return 'Manual refund cases';
  if (/^\/master-admin\/operations\/order-ledger\/[^/]+$/.test(pathname)) return 'Order ledger';
  if (pathname === '/operations/vendor-policy-queries') return 'Vendor policy queries';
  if (pathname === '/operations/vendor-contact-queries') return 'Vendor contact queries';
  if (pathname === '/operations/legal-policies/new') return 'Create legal policy version';
  if (/^\/operations\/legal-policies\/[^/]+\/edit$/.test(pathname)) return 'Edit legal policy draft';
  if (/^\/operations\/legal-policies\/[^/]+\/acceptances$/.test(pathname)) return 'Policy acceptances';
  if (pathname === '/operations/legal-policies') return 'Legal & policy versions';
  if (pathname === '/operations/instant-consultation/sections') {
    return 'Instant consultation sections';
  }
  if (pathname === '/operations/instant-consultation/specialities') {
    return 'Instant consultation specialities';
  }
  if (pathname === '/vendor-form-catalog') return 'Vendor form catalog';
  if (pathname === '/vendor-profile-updates') return 'Vendor profile updates';

  if (/^\/vendors\/[^/]+\/[^/]+\/event-timeline$/.test(pathname)) {
    return 'Vendor event timeline';
  }
  if (/^\/vendors\/[^/]+\/[^/]+\/login-activity$/.test(pathname)) {
    return 'Vendor login activity';
  }
  if (/^\/vendors\/ambulance\/[^/]+\/booking\/[^/]+$/.test(pathname)) {
    return 'Ambulance booking';
  }
  if (/^\/vendors\/ambulance\/[^/]+\/details$/.test(pathname)) {
    return 'Ambulance vendor details';
  }
  if (/^\/vendors\/medical-store\/[^/]+\/order\/[^/]+$/.test(pathname)) {
    return 'Medicine order';
  }
  if (/^\/vendors\/medical-store\/[^/]+\/details$/.test(pathname)) {
    return 'Medical store vendor details';
  }
  if (/^\/vendors\/doctor-clinic\/[^/]+\/details$/.test(pathname)) {
    return 'Doctor clinic vendor details';
  }
  if (/^\/vendors\/doctor-clinic\/[^/]+\/timeslots$/.test(pathname)) {
    return 'Doctor clinic timeslots';
  }

  const vendorDashboard = pathname.match(
    /^\/vendors\/(hospital|ambulance|medical-store|blood-bank|doctor-clinic|diagnostic-center|product)\/[^/]+$/
  );
  if (vendorDashboard) {
    const labels: Record<string, string> = {
      hospital: 'Hospital vendor',
      ambulance: 'Ambulance vendor',
      'medical-store': 'Medical store vendor',
      'blood-bank': 'Blood bank vendor',
      'doctor-clinic': 'Doctor clinic vendor',
      'diagnostic-center': 'Diagnostic center vendor',
      product: 'Product partner vendor',
    };
    return labels[vendorDashboard[1]] ?? 'Vendor';
  }

  if (/^\/users\/[^/]+\/sessions\/[^/]+$/.test(pathname)) {
    return 'Session detail';
  }
  if (/^\/users\/[^/]+$/.test(pathname)) {
    return 'User analytics';
  }

  if (/^\/employees\/sales-executive\/[^/]+$/.test(pathname)) {
    return 'Sales executive';
  }

  if (pathname === '/operations/deep-links') return 'Deep link management';
  if (pathname === '/operations/booking-offers') return 'Offers and discounts';
  if (pathname === '/operations/membership' || pathname.startsWith('/operations/membership/')) {
    if (pathname.includes('/plans')) return 'Membership plans';
    if (pathname.includes('/members')) return 'Membership members';
    if (pathname.includes('/transfers')) return 'Membership transfers';
    if (pathname.includes('/reports')) return 'Membership reports';
    if (pathname.includes('/settings')) return 'Membership settings';
    if (pathname.includes('/tools')) return 'Membership tools';
    return 'Membership overview';
  }
  if (pathname === '/operations/media-management') return 'Media management';

  return '';
}
